'use client';

import { useMemo, useState, type ReactNode } from 'react';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import {
  ArrowDown01Icon,
  ArrowRight01Icon,
  Cancel01Icon,
} from '@terra/ui-icons';
import { Collapsible } from '../../../components/collapsible';
import { MetaDefault } from '../../../components/typography';
import { MarkdownPreview } from '../../markdown-preview/MarkdownPreview';
import { ReadOnlyCodeEditor } from '../../chat/components/ReadOnlyCodeEditor';
import { TraceReasoningCard } from '../../chat/trace-view/components/TraceReasoningCard';
import { TraceToolCard } from '../../chat/trace-view/TraceToolCard';
import type { ProbeTraceStepDto } from '@terra/shared-contracts-assets';
import { UnverifiedAssetProbeExecutionCard } from './UnverifiedAssetProbeExecutionCard';
import { UnverifiedAssetProbeHttpExchange } from './UnverifiedAssetProbeHttpExchange';
import { parseProbeHttpExchange } from './probe-trace-http.utils';
import {
  buildProbeReasoningMarkdown,
  buildProbeSearchToolCall,
  resolveProbeTraceStepPresentation,
} from './probe-trace-step-presentation.utils';
import { UNVERIFIED_ASSET_MARKDOWN_FONT_SIZE } from './unverified-asset-typography';

type UnverifiedAssetProbeTraceStepProps = {
  step: ProbeTraceStepDto;
};

function ProbeSimpleStepRow({
  title,
  hasError,
  canExpand,
  children,
}: {
  title: string;
  hasError: boolean;
  canExpand: boolean;
  children?: ReactNode;
}) {
  const [isOpen, setIsOpen] = useState(false);

  if (!canExpand) {
    return (
      <HStack w="full" gap={1.5} alignItems="center" py={1} borderRadius="sm">
        <MetaDefault color={hasError ? 'fg.error' : 'fg.muted'}>
          {title}
        </MetaDefault>
        <Box flex={1} />
        {hasError ? (
          <Box color="fg.error" display="flex" alignItems="center">
            <Cancel01Icon size={10} />
          </Box>
        ) : null}
      </HStack>
    );
  }

  return (
    <Collapsible.Root
      open={isOpen}
      onOpenChange={({ open }) => setIsOpen(open)}
      lazyMount
      unmountOnExit
    >
      <Collapsible.Trigger asChild>
        <HStack
          w="full"
          gap={1.5}
          alignItems="center"
          py={1}
          cursor="pointer"
          borderRadius="sm"
        >
          <MetaDefault color={hasError ? 'fg.error' : 'fg.muted'}>
            {title}
          </MetaDefault>
          <Box display="flex" alignItems="center" color="fg.muted">
            {isOpen ? (
              <ArrowDown01Icon size={12} />
            ) : (
              <ArrowRight01Icon size={12} />
            )}
          </Box>
          <Box flex={1} />
          {hasError ? (
            <Box color="fg.error" display="flex" alignItems="center">
              <Cancel01Icon size={10} />
            </Box>
          ) : null}
        </HStack>
      </Collapsible.Trigger>
      <Collapsible.Content>
        <Box pt={1} pb={2} width="100%">
          {children}
        </Box>
      </Collapsible.Content>
    </Collapsible.Root>
  );
}

export function UnverifiedAssetProbeTraceStep({
  step,
}: UnverifiedAssetProbeTraceStepProps) {
  const exchange = parseProbeHttpExchange({ step });
  const hasError = step.status === 'FAILED' || step.status === 'BLOCKED';
  const presentation = resolveProbeTraceStepPresentation({
    step,
    hasHttpExchange: Boolean(exchange),
  });
  const [showHttpView, setShowHttpView] = useState(Boolean(exchange));
  const reasoningMarkdown = useMemo(
    () => buildProbeReasoningMarkdown({ step }),
    [step],
  );
  const searchToolCall = useMemo(
    () => buildProbeSearchToolCall({ step }),
    [step],
  );

  if (presentation === 'reasoning') {
    return (
      <Box width="100%">
        <TraceReasoningCard
          text={reasoningMarkdown}
          contentFontSize={UNVERIFIED_ASSET_MARKDOWN_FONT_SIZE}
        />
      </Box>
    );
  }

  if (presentation === 'tool') {
    return (
      <Box width="100%">
        <TraceToolCard toolCall={searchToolCall} tracePhase="attack" />
      </Box>
    );
  }

  if (presentation === 'http') {
    return (
      <VStack gap={2} alignItems="stretch" w="full">
        {step.summary ? (
          <Box width="100%" color="fg.muted">
            <MarkdownPreview
              source={step.summary}
              fontSize={UNVERIFIED_ASSET_MARKDOWN_FONT_SIZE}
            />
          </Box>
        ) : null}
        <UnverifiedAssetProbeExecutionCard
          title={step.title}
          code={step.code}
          result={step.result}
          showHttpView={Boolean(exchange && showHttpView)}
          onShowCode={() => setShowHttpView(false)}
          onShowHttp={() => setShowHttpView(true)}
          canToggleHttp={Boolean(exchange)}
          hasError={hasError}
        >
          {exchange ? (
            <UnverifiedAssetProbeHttpExchange exchange={exchange} embedded />
          ) : null}
        </UnverifiedAssetProbeExecutionCard>
      </VStack>
    );
  }

  const canExpandDetail = Boolean(step.result?.trim());
  const detailBody = canExpandDetail ? (
    <ReadOnlyCodeEditor
      value={step.result ?? ''}
      maxHeight="240px"
      minHeight="44px"
      language="text"
    />
  ) : null;

  return (
    <VStack gap={2} alignItems="stretch" w="full">
      {step.summary ? (
        <Box width="100%" color="fg.muted">
          <MarkdownPreview
            source={step.summary}
            fontSize={UNVERIFIED_ASSET_MARKDOWN_FONT_SIZE}
          />
        </Box>
      ) : null}
      <ProbeSimpleStepRow
        title={step.title}
        hasError={hasError}
        canExpand={canExpandDetail}
      >
        {detailBody}
      </ProbeSimpleStepRow>
    </VStack>
  );
}
